import { PAGE_SIZE } from '@/constants/apiPageSize'
import api from '@/services/api'
import endpoints from '@/services/endpoints'
import {
  AnalyticsExport,
  AnalyticsExportResponse,
  BrandStyles,
  Campaign,
  CampaignAction,
  CampaignAnalyticsOptions,
  CampaignLink,
  CampaignLinkSessionsResponse,
  CampaignLinksResponse,
  CampaignModalitiesOverridesResponse,
  CampaignProductOverride,
  CampaignProductOverrideOrderUpdatePayload,
  CampaignProductOverridePayload,
  CampaignProductOverridesResponse,
  CampaignScreenshotsResponse,
  CampaignsResponse,
  CampaignStatus,
  CampaignStyle,
  CreateAnalyticsExportPayload,
  CreateCampaignLinkPayload,
  CreateCampaignPayload,
  CreateRetailerBucketsPayload,
  ProductChainsPayload,
  ProductChainsResponse,
  ProductRetailersPayload,
  ProductRetailersResponse,
  ProductRuleAnalyticsResponse,
  ProductRulePayload,
  RetailerBucket,
  RetailerBucketsResponse,
  SelectedProductForCampaign,
  SessionRecordingTimeframeOptions,
  UpdateProductRulePayload,
  UpdateRetailerBucketPayload,
  UpdateSessionResponse,
} from '@/types/campaign'

const getCampaigns = async (options: {
  page: number
  search?: string
  status?: CampaignStatus
  brand?: number
}): Promise<CampaignsResponse> => {
  const response = await api.get(endpoints.CAMPAIGNS.GET_CAMPAIGNS, {
    params: {
      page_size: 10,
      page: options.page,
      ...(options.search && { search: options.search }),
      ...(options.status && { status: options.status }),
      ...(options.brand && { account: options.brand }),
    },
  })
  return response.data
}

const getCampaignDetails = async (
  campaignId: number,
): Promise<Campaign & { products: SelectedProductForCampaign[] }> => {
  const response = await api.get(endpoints.CAMPAIGNS.GET_CAMPAIGN_DETAILS.replace(':campaignId', String(campaignId)))
  return response.data
}

const getCampaignLinks = async (page: number, campaignId: number): Promise<CampaignLinksResponse> => {
  const response = await api.get(endpoints.CAMPAIGNS.GET_CAMPAIGN_LINKS.replace(':campaignId', String(campaignId)), {
    params: {
      page_size: PAGE_SIZE,
      page,
    },
  })
  return response.data
}

const getCampaignLink = async (campaignId: number, campaignLinkId: number): Promise<CampaignLink> => {
  const response = await api.get(
    endpoints.CAMPAIGNS.GET_CAMPAIGN_LINK.replace(':campaignId', String(campaignId)).replace(
      ':campaignLinkId',
      String(campaignLinkId),
    ),
  )
  return response.data
}

const createLink = async (campaignId: number, payload: CreateCampaignLinkPayload): Promise<CampaignLink> => {
  const response = await api.post(endpoints.CAMPAIGNS.CREATE_LINK.replace(':campaignId', String(campaignId)), payload)
  return response.data
}

const updateLink = async (
  campaignId: number,
  campaignLinkId: number,
  payload: Partial<CreateCampaignLinkPayload>,
): Promise<CampaignLink> => {
  const response = await api.patch(
    endpoints.CAMPAIGNS.UPDATE_LINK.replace(':campaignId', String(campaignId)).replace(
      ':campaignLinkId',
      String(campaignLinkId),
    ),
    payload,
  )
  return response.data
}

const deleteLink = async (campaignId: number, campaignLinkId: number) => {
  const response = await api.delete(
    endpoints.CAMPAIGNS.DELETE_LINK.replace(':campaignId', String(campaignId)).replace(
      ':campaignLinkId',
      String(campaignLinkId),
    ),
  )
  return response.data
}

const getBrandStyle = async (options: { entityId: number }): Promise<BrandStyles> => {
  const response = await api.get(endpoints.CAMPAIGNS.GET_BRAND_STYLE.replace(':entityId', String(options.entityId)))
  return response.data
}

const updateBrandStyle = async (
  brandStyleId: number,
  payload: {
    attributes: Partial<Omit<CampaignStyle, 'no_result'>> & {
      no_result: Partial<CampaignStyle['no_result']>
    }
  },
): Promise<BrandStyles> => {
  const response = await api.patch(
    endpoints.CAMPAIGNS.UPDATE_BRAND_STYLE.replace(':brandStyleId', String(brandStyleId)),
    payload,
  )
  return response.data
}

const createCampaign = async (
  payload: CreateCampaignPayload,
): Promise<Campaign & { products: SelectedProductForCampaign[] }> => {
  const response = await api.post(endpoints.CAMPAIGNS.CREATE_CAMPAIGN, payload)
  return response.data
}

const updateCampaign = async (
  campaignId: number,
  payload: Partial<CreateCampaignPayload>,
): Promise<Campaign & { products: SelectedProductForCampaign[] }> => {
  const response = await api.patch(
    endpoints.CAMPAIGNS.UPDATE_CAMPAIGN.replace(':campaignId', String(campaignId)),
    payload,
  )
  return response.data
}

const changeCampaignStatus = async (campaignId: number, action: CampaignAction) => {
  const response = await api.post(
    endpoints.CAMPAIGNS.CHANGE_CAMPAIGN_STATUS.replace(':campaignId', String(campaignId)),
    {
      action,
    },
  )
  return response.data
}

const getAnalytics = async <T>(options: CampaignAnalyticsOptions): Promise<T> => {
  const response = await api.get(endpoints.CAMPAIGNS.GET_ANALYTICS.replace(':type', options.type), {
    params: {
      account_id: options.accountId,
      calendar_interval: 'interval' in options ? options.interval : undefined,
      start_date: options.startDate,
      end_date: options.endDate,
      campaign_id: 'campaignId' in options ? options.campaignId : undefined,
      link_id: options.linkId,
      source_type: 'sourceType' in options ? options.sourceType : undefined,
      prop: 'propertyType' in options ? options.propertyType : undefined,
      'searched_location.region': 'region' in options ? options.region : undefined,
      page: 'page' in options ? options.page : undefined,
      page_size: 'pageSize' in options ? options.pageSize : undefined,
      sort_by: 'sortBy' in options ? options.sortBy : undefined,
      sort_order: 'sortOrder' in options ? options.sortOrder : undefined,
      retailer_id: 'retailerId' in options ? options.retailerId : undefined,
    },
  })
  return response.data
}

const getProductRuleAnalytics = async (payload: ProductRulePayload): Promise<ProductRuleAnalyticsResponse> => {
  const response = await api.post(endpoints.CAMPAIGNS.GET_PRODUCT_RULE_ANALYTICS, payload)
  return response.data
}

const updateProductRules = async (campaignId: number, payload: UpdateProductRulePayload) => {
  const response = await api.put(
    endpoints.CAMPAIGNS.UPDATE_PRODUCT_RULES.replace(':campaignId', String(campaignId)),
    payload,
  )
  return response.data
}

const getRetailerBuckets = async (campaignId: number, productId: number): Promise<RetailerBucketsResponse> => {
  const endpoint = endpoints.CAMPAIGNS.GET_RETAILER_BUCKETS.replace(':campaignId', String(campaignId)).replace(
    ':productId',
    String(productId),
  )

  const response = await api.get(endpoint)
  return response.data
}

const createRetailerBuckets = async (options: {
  campaignId: number
  productId: number
  payload: CreateRetailerBucketsPayload
}): Promise<{ buckets: RetailerBucket[] }> => {
  const endpoint = endpoints.CAMPAIGNS.CREATE_RETAILER_BUCKET.replace(
    ':campaignId',
    String(options.campaignId),
  ).replace(':productId', String(options.productId))

  const response = await api.post(endpoint, options.payload)
  return response.data
}

const updateRetailerBucket = async (options: {
  campaignId: number
  productId: number
  bucketId: number
  payload: Partial<UpdateRetailerBucketPayload>
}): Promise<RetailerBucket> => {
  const endpoint = endpoints.CAMPAIGNS.UPDATE_RETAILER_BUCKET.replace(':campaignId', String(options.campaignId))
    .replace(':productId', String(options.productId))
    .replace(':bucketId', String(options.bucketId))

  const response = await api.patch(endpoint, options.payload)
  return response.data
}

const deleteRetailerBucket = async (options: { campaignId: number; productId: number; bucketId: number }) => {
  const endpoint = endpoints.CAMPAIGNS.UPDATE_RETAILER_BUCKET.replace(':campaignId', String(options.campaignId))
    .replace(':productId', String(options.productId))
    .replace(':bucketId', String(options.bucketId))

  const response = await api.delete(endpoint)
  return response.data
}

const getRetailersForProduct = async (payload: ProductRetailersPayload): Promise<ProductRetailersResponse> => {
  const response = await api.post(endpoints.CAMPAIGNS.GET_RETAILERS_FOR_PRODUCT, payload)
  return response.data
}

const getChainsForProduct = async (payload: ProductChainsPayload): Promise<ProductChainsResponse> => {
  const response = await api.post(endpoints.CAMPAIGNS.GET_CHAINS_FOR_PRODUCT, payload)
  return response.data
}

const getCampaignLinkSessions = async (options: {
  linkId: string
  timeframe: SessionRecordingTimeframeOptions
  offset?: number
}): Promise<CampaignLinkSessionsResponse> => {
  const response = await api.get(endpoints.CAMPAIGNS.GET_LINK_SESSION_RECORDINGS.replace(':linkId', options.linkId), {
    params: {
      timeframe: options.timeframe,
      offset: options.offset,
    },
  })
  return response.data
}

const updateSession = async (sessionId: string): Promise<UpdateSessionResponse> => {
  const response = await api.post(endpoints.CAMPAIGNS.SHARE_SESSION_RECORDING.replace(':recordingId', sessionId))
  return response.data
}

const unshareSession = async (sessionId: string): Promise<void> => {
  await api.post(endpoints.CAMPAIGNS.UNSHARE_SESSION_RECORDING.replace(':recordingId', sessionId))
}

const getCampaignScreenshots = async (campaignId: number): Promise<CampaignScreenshotsResponse> => {
  const response = await api.get(endpoints.CAMPAIGNS.GET_SCREENSHOTS.replace(':campaignId', String(campaignId)))
  return response.data
}

const getCampaignProductOverrides = async (
  campaignId: number,
  productId: number,
): Promise<CampaignProductOverridesResponse> => {
  const response = await api.get(
    endpoints.CAMPAIGNS.GET_CAMPAIGN_PRODUCT_OVERRIDES.replace(':campaignId', String(campaignId)).replace(
      ':productId',
      String(productId),
    ),
  )
  return response.data
}

const createCampaignProductOverride = async (
  campaignId: number,
  productId: number,
  payload: CampaignProductOverridePayload | FormData,
): Promise<CampaignProductOverride> => {
  const response = await api.post(
    endpoints.CAMPAIGNS.CREATE_CAMPAIGN_PRODUCT_OVERRIDE.replace(':campaignId', String(campaignId)).replace(
      ':productId',
      String(productId),
    ),
    payload,
  )
  return response.data
}

const updateCampaignProductOverride = async (
  campaignId: number,
  productId: number,
  overrideId: number,
  payload: CampaignProductOverridePayload | CampaignProductOverrideOrderUpdatePayload,
) => {
  const response = await api.patch(
    endpoints.CAMPAIGNS.UPDATE_CAMPAIGN_PRODUCT_OVERRIDE.replace(':campaignId', String(campaignId))
      .replace(':productId', String(productId))
      .replace(':overrideId', String(overrideId)),
    payload,
  )
  return response.data
}

const deleteCampaignProductOverride = async (campaignId: number, productId: number, overrideId: number) => {
  const response = await api.delete(
    endpoints.CAMPAIGNS.DELETE_CAMPAIGN_PRODUCT_OVERRIDE.replace(':campaignId', String(campaignId))
      .replace(':productId', String(productId))
      .replace(':overrideId', String(overrideId)),
  )
  return response.data
}

const getCampaignModalityOverride = async (campaignId: number): Promise<CampaignModalitiesOverridesResponse> => {
  const response = await api.get(
    endpoints.CAMPAIGNS.GET_CAMPAIGN_MODALITY_OVERRIDE.replace(':campaignId', String(campaignId)),
  )
  return response.data
}

const createCampaignModalityOverride = async (
  campaignId: number,
  payload: { modality_id: number; order: number }[],
) => {
  const response = await api.post(
    endpoints.CAMPAIGNS.CREATE_CAMPAIGN_MODALITY_OVERRIDE.replace(':campaignId', String(campaignId)),
    payload,
  )
  return response.data
}

const updateCampaignModalityOverride = async (
  campaignId: number,
  payload: { modality_id: number; order: number; name?: string; attributes?: { background_color: string } }[],
) => {
  const response = await api.patch(
    endpoints.CAMPAIGNS.UPDATE_CAMPAIGN_MODALITY_OVERRIDE.replace(':campaignId', String(campaignId)),
    payload,
  )
  return response.data
}

const createAnalyticsExport = async (payload: CreateAnalyticsExportPayload): Promise<AnalyticsExport> => {
  const response = await api.post(endpoints.CAMPAIGNS.CREATE_ANALYTICS_EXPORT, payload)
  return response.data
}

const getAnalyticsExports = async (options: { page: number; account?: number }): Promise<AnalyticsExportResponse> => {
  const response = await api.get(endpoints.CAMPAIGNS.GET_ANALYTICS_EXPORTS, {
    params: {
      page: options.page,
      account: options.account,
    },
  })
  return response.data
}

export default {
  getCampaigns,
  getCampaignDetails,
  getCampaignLinks,
  getCampaignLink,
  createLink,
  updateLink,
  deleteLink,
  getBrandStyle,
  updateBrandStyle,
  createCampaign,
  updateCampaign,
  changeCampaignStatus,
  getAnalytics,
  getProductRuleAnalytics,
  updateProductRules,
  getRetailerBuckets,
  createRetailerBuckets,
  updateRetailerBucket,
  deleteRetailerBucket,
  getRetailersForProduct,
  getCampaignLinkSessions,
  updateSession,
  unshareSession,
  getCampaignScreenshots,
  getCampaignProductOverrides,
  createCampaignProductOverride,
  updateCampaignProductOverride,
  deleteCampaignProductOverride,
  getCampaignModalityOverride,
  createCampaignModalityOverride,
  updateCampaignModalityOverride,
  getChainsForProduct,
  createAnalyticsExport,
  getAnalyticsExports,
}
